import {Component, HostListener, OnDestroy, OnInit, Optional} from '@angular/core';
import {Ast, ExpressionCompilerService} from '@ngmedax/expression-compiler';
import {NgbActiveModal, NgbModal, NgbModalRef} from '@ng-bootstrap/ng-bootstrap';
import {Translatable, TranslationService} from '@ngmedax/translation';
import {Subscription} from 'rxjs';
import {TRANSLATION_EDITOR_SCOPE} from '../../../constants';
import {KEYS} from '../../../translation-keys';
import {TEMPLATE_VARIABLE_GROUPS} from '../../froala/template-variables';
import {TemplateVariablePickerComponent} from '../template-variable-picker/template-variable-picker.component';
import {QuestionnaireConditionService} from '../../services/questionnaire-condition.service';
import {DependencyCatalogService} from './dependency-catalog.service';
import {canNegateDependency, DependencySource, DEPENDENCY_OPERATORS, dependencyIssue, dependencyRuleIssue, dependencySource,
  dependencySummary, dependencyValueType, emptyDependency, negateDependency, newDependencyRule} from './dependency-conditions';

export interface DependencyEditorComponent extends Translatable {}

/** Edits a detached visibility draft with readable sources, typed values and explicit application. */
@Component({
  selector: 'app-qa-dependency-editor', templateUrl: './dependency-editor.component.html',
  styleUrls: ['../template-condition-editor/template-condition-editor.component.css', './dependency-editor.component.css']
})
@Translatable({scope: TRANSLATION_EDITOR_SCOPE, keys: KEYS})
export class DependencyEditorComponent implements OnInit, OnDestroy {
  public group: Ast.Group = emptyDependency();
  public sources: DependencySource[] = [];
  public locale = 'de_DE';
  public originalSource = '';
  public existing = false;
  public advanced = false;
  public operators = DEPENDENCY_OPERATORS;
  private picker: NgbModalRef;
  private subscription: Subscription;

  /** Injects the shared source picker and the same compiler used by questionnaire visibility. */
  public constructor(public activeModal: NgbActiveModal, private modals: NgbModal, public compiler: ExpressionCompilerService,
    private catalog: DependencyCatalogService, private conditions: QuestionnaireConditionService,
    @Optional() private translationService: TranslationService) {}

  /** Refreshes references after external edits without deleting any draft rule. */
  public ngOnInit(): void {
    this.sources = this.catalog.build(this.locale);
    this.subscription = this.conditions.onUpdateSelect.subscribe(() => this.sources = this.catalog.build(this.locale));
  }

  /** Releases subscriptions and closes a nested picker with its owner. */
  public ngOnDestroy(): void { this.subscription?.unsubscribe(); this.picker?.dismiss(); }

  /** Resolves display metadata while retaining any missing reference in the draft. */
  public source(rule: Ast.Expression): DependencySource { return dependencySource(rule.variable, this.sources); }

  /** Uses friendly titles with a technical fallback for broken references. */
  public name(rule: Ast.Expression): string { return this.source(rule)?.label || rule.variable || this._('Frage oder Variable wählen'); }

  /** Opens the existing searchable picker with dependency-specific wording and answer hierarchy. */
  public choose(rule: Ast.Expression): void {
    const modal = this.modals.open(TemplateVariablePickerComponent, {
      centered: true, size: 'lg', ariaLabelledBy: 'qa-template-variable-title'
    });
    this.picker = modal;
    Object.assign(modal.componentInstance, {
      variables: this.sources, title: 'Frage oder Variable wählen', showPaths: false, allLabel: 'Alle',
      hint: 'Suchen Sie nach einer Frage, Antwort oder Variable.',
      footer: 'Die Bedingung verwendet den Wert beim Ausfüllen.',
      groups: [{id: 'answers', label: 'Fragen und Antworten', icon: 'fa-check-square-o'}, ...TEMPLATE_VARIABLE_GROUPS]
    });
    modal.result.then((source: DependencySource) => {
      this.picker = null;
      if (rule.variable === source.path) { return; }
      rule.variable = source.path;
      rule.operator = '==';
      rule.value = source.valueType === 'boolean' ? true : source.valueType === 'number' ? null : '';
    }, () => this.picker = null);
  }

  /** Appends a rule or a nested all-of group to the detached tree. */
  public add(group: Ast.Group, nested = false): void {
    group.elements.push(nested ? {...emptyDependency(), elements: [newDependencyRule()]} : newDependencyRule());
  }

  /** Removes a draft node without mutating the stored questionnaire. */
  public remove(group: Ast.Group, index: number): void { group.elements.splice(index, 1); }

  /** Inverts a group in place, pattern comparisons cannot be inverted */
  public canNegate(group: Ast.Group): boolean { return canNegateDependency(group); }

  public negate(group: Ast.Group): void { if (canNegateDependency(group)) { negateDependency(group); } }

  /** Deliberately removes an unsupported expression instead of silently replacing it on opening. */
  public clear(): void { this.group = emptyDependency(); this.originalSource = ''; }

  /** Exposes only useful comparisons while keeping any existing advanced operator selectable. */
  public comparisons(rule: Ast.Expression): string[] {
    const type = this.type(rule);
    const operators = type === 'boolean' ? ['==', '!='] : type === 'text' ? ['==', '!='] : ['==', '!=', '>', '>=', '<', '<='];
    if (this.advanced) { Object.keys(this.operators).forEach(operator => operators.indexOf(operator) === -1 && operators.push(operator)); }
    if (operators.indexOf(rule.operator) === -1) { operators.push(rule.operator); }
    return operators;
  }

  /** Keeps original value types stable rather than coercing legacy comparisons during editing. */
  public type(rule: Ast.Expression): string { return rule.operator === '~' ? 'text' : dependencyValueType(rule, this.sources); }

  /** Switches regex operands to text only after an explicit operator change. */
  public changeOperator(rule: Ast.Expression): void {
    if (rule.operator === '~' && typeof rule.value !== 'string') { rule.value = ''; }
  }

  /** Offers a compact answer predicate without conflating false with an unset answer. */
  public isAnswer(rule: Ast.Expression): boolean {
    return this.source(rule)?.group === 'answers' && typeof rule.value === 'boolean' && ['==', '!='].indexOf(rule.operator) !== -1;
  }

  /** Encodes the exact predicate so legacy false comparisons retain their semantics. */
  public predicate(rule: Ast.Expression): string { return `${rule.operator}:${rule.value}`; }

  /** Changes a selected-answer predicate only after an explicit user action. */
  public setPredicate(rule: Ast.Expression, value: string): void {
    rule.operator = value.split(':')[0] as Ast.OPERATOR;
    rule.value = value.split(':')[1] === 'true';
  }

  /** Preserves configured date precision and existing timezone information until the user edits. */
  public dateValue(rule: Ast.Expression): string {
    if (!rule.value) { return ''; }
    const value = rule.value as any;
    if (value instanceof Date) { return new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, -1); }
    return String(value);
  }

  /** Keeps full timestamps editable even if a source is currently configured as date-only. */
  public dateInput(rule: Ast.Expression): string {
    return (rule.value as any) instanceof Date ? 'datetime-local' : this.source(rule)?.dateInput || 'date';
  }

  /** Stores date-only values as strings and full timestamps as dates like the existing editor. */
  public setDate(rule: Ast.Expression, value: string): void {
    rule.value = this.dateInput(rule) === 'datetime-local' && value ? new Date(value) as any : String(value ?? '');
  }

  /** Provides the first actionable validation message for each rule. */
  public issue(rule: Ast.Expression): string { return dependencyRuleIssue(rule, this.sources, this.compiler); }

  /** Disables applying incomplete or unsupported expressions. */
  public get validation(): string { return dependencyIssue(this.group, this.sources, this.compiler); }

  /** Shows the exact expression only when it can be compiled safely. */
  public get expression(): string {
    return !this.group ? this.originalSource : !this.validation && this.group.elements.length ? this.compiler.compile(this.group) : '';
  }

  /** Summarizes nested groups with explicit grouping and friendly source names. */
  public get summary(): string { return dependencySummary(this.group, this.sources, text => this._(text)); }

  /** Applies the complete draft as one change. */
  public save(): void { if (!this.validation) { this.activeModal.close(this.group); } }

  /** Keeps global questionnaire save shortcuts from bypassing the draft's apply button. */
  @HostListener('keydown', ['$event'])
  public keydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') { event.preventDefault(); event.stopPropagation(); }
  }
}
